import { Logger } from "../../../../../../../lib/logger/Logger";
import {
	CustomTable,
	BulkOpsState,
} from "../../../../../components/custom-table/custom-table.component";
import { TableConfig } from "../../../../../types/types";
import { jobItemsTableConfigDefault } from "./job-items-table-config-default";
import { JobItemsTariffsSelection } from "./job-items-table-components/job-item-tariffs-selection.component";
import { JobItemsArticlesSelection } from "./job-items-table-components/job-items-articles-selection.component";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { RoundToType } from "@/dentlab/src/store/Jobs/utils";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { JobStatusEnum } from "@/lib/types/job";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { JobDocumentTypeEnum } from "../../job-document.types";
import { CodeSuggestions } from "./code-suggestions.component";
import { useTemplatesStore } from "@/dentlab/src/store/Templates";
import { TemplateSelectDialog } from "../../../job-page-components/template-select-dialog.component";
import {
	TariffWithPricesViewType,
	useCatalogsStore,
} from "@/dentlab/src/store/Catalogs";
import { ExtendedArticleEntityType } from "@/dentlab/src/store/Jobs/types";
import { TariffSelectionDialog } from "./job-items-table-components/tariff-selection-dialog.component";
import { useClipboard } from "@/dentlab/src/context/ClipboardContext";
import {
	Calculator,
	ClipboardPaste,
	List,
	Keyboard,
	FolderTree,
	X,
} from "lucide-react";
import { Button } from "@/dentlab/src/components/ui/button";
import { Input } from "@/dentlab/src/components/ui/input";
import { useShortCutListener } from "@/dentlab/src/hooks/useShortcutListener";
import { JobItemsShortcutsDialog } from "@/dentlab/src/components/job-items-shortcuts-dialog";
import { JobItemsServiceCatalogSelection } from "./job-items-table-components/job-items-service-catalog-selection.component";
import { DefaultTechnicianSelect } from "./job-items-table-components/default-technician-select.component";
import { hasConfigValue } from "@/dentlab/src/layout/layout-components/app-bar/DlSideNavigation";
import { useServiceCatalogStore } from "@/dentlab/src/store/ServiceCatalog";

function addJobItemAllowed(status: JobStatusEnum) {
	return status === JobStatusEnum.IN_PROGRESS;
}

export const JobItemsTable: React.FC<{
	jobDocumentId: number;
	status: JobStatusEnum;
	jobDocumentType: JobDocumentTypeEnum;
	disabled?: boolean;
	total?: { value: number; text: string };
}> = ({ jobDocumentId, jobDocumentType, status, disabled = false, total }) => {
	const [showShortcuts, setShowShortcuts] = useState(false);

	const { gridConfig, updateGridConfig } = useCentralStore((state) => ({
		gridConfig: state.gridConfig,
		updateGridConfig: state.updateGridConfig,
	}));

	const {
		deleteJobItems,
		pasteJobItems,
		changeJobItem,
		jobItemsForDocuments,
		fetchJobItems,
		addJobItem,
		itemSelectionDialog,
		handleItemSelection,
		performUndo,
		performRedo,
		canUndo,
		canRedo,
		upsertJobItems,
		setJobItemsForDocument,
		reorderJobItems,
		requireJobPricing,
	} = useJobStore((state) => ({
		jobItemsForDocuments: state.jobItemsForDocuments,
		deleteJobItems: state.deleteJobItems,
		pasteJobItems: state.pasteJobItems,
		changeJobItem: state.changeJobItem,
		fetchJobItems: state.fetchJobItems,
		addJobItem: state.addJobItem,
		itemSelectionDialog: state.itemSelectionDialog,
		handleItemSelection: state.handleItemSelection,
		performUndo: state.performUndo,
		performRedo: state.performRedo,
		canUndo: state.canUndo,
		canRedo: state.canRedo,
		upsertJobItems: state.upsertJobItems,
		setJobItemsForDocument: state.setJobItemsForDocument,
		reorderJobItems: state.reorderJobItems,
		requireJobPricing: state.requireJobPricing,
	}));

	const { tariffsLookup } = useCatalogsStore((state) => ({
		tariffsLookup: state.tariffsLookup,
	}));

	// Flatten the nested tariffs structure for CodeSuggestions
	const tariffs = useMemo(() => {
		const flattened: TariffWithPricesViewType[] = [];
		for (const codeE in tariffsLookup) {
			for (const id in tariffsLookup[codeE]) {
				flattened.push(tariffsLookup[codeE][id]);
			}
		}
		return flattened;
	}, [tariffsLookup]);

	// Check if clipboard has job items
	const { clipboardData } = useClipboard();

	// For the big toolbar button: 2-minute time limit
	const hasRecentJobItemsInClipboard = useMemo(() => {
		if (!clipboardData.timestamp || clipboardData.data.length === 0) {
			return false;
		}
		if (!clipboardData.type.includes("job_items")) {
			return false;
		}
		const twoMinutesInMs = 2 * 60 * 1000;
		const isRecent = Date.now() - clipboardData.timestamp < twoMinutesInMs;
		return isRecent;
	}, [clipboardData]);

	const historyOperationInProgressRef = useRef(false);

	useEffect(() => {
		fetchJobItems(jobDocumentId);
	}, []);

	useShortCutListener((key) => {
		switch (key) {
			case "z":
				handleUndo();
				break;
			case "redo":
				handleRedo();
				break;
			default:
				break;
		}
	});

	const jobItems = jobItemsForDocuments?.[jobDocumentId] ?? [];

	const handleRowChange = (newRow: any, field?: string) => {
		if (addJobItemAllowed(status)) changeJobItem(newRow, field);
	};

	const handleUndo = () => {
		if (!canUndo() || historyOperationInProgressRef.current) return;

		historyOperationInProgressRef.current = true;

		const currentItems = jobItemsForDocuments?.[jobDocumentId] ?? [];
		const previousState = performUndo(jobDocumentId, currentItems);

		if (previousState) {
			setJobItemsForDocument(jobDocumentId, previousState);
			upsertJobItems(jobDocumentId, "Error during undo", true);
		}

		setTimeout(() => {
			historyOperationInProgressRef.current = false;
		}, 50);
	};

	const handleRedo = () => {
		if (!canRedo() || historyOperationInProgressRef.current) return;

		historyOperationInProgressRef.current = true;

		const currentItems = jobItemsForDocuments?.[jobDocumentId] ?? [];
		const nextState = performRedo(jobDocumentId, currentItems);

		if (nextState) {
			setJobItemsForDocument(jobDocumentId, nextState);
			upsertJobItems(jobDocumentId, "Error during redo", true);
		}

		setTimeout(() => {
			historyOperationInProgressRef.current = false;
		}, 50);
	};

	const handleAddTemplate = (value: string, options?: { force: boolean }) => {
		if (!canAddItems) {
			showNotification({
				message: "Vorlagen können nicht hinzugefügt werden",
				type: "error",
			});
			return;
		}
		if (addJobItemAllowed(status)) {
			const templates = useTemplatesStore.getState().templatesLookup;
			const template = Object.values(templates).find(
				(t) => t.code === value
			);

			if (!template) {
				showNotification({
					message: "Vorlage nicht gefunden",
					type: "error",
				});
				return;
			}

			// Checked once here: each addJobItem below would otherwise toast on its own.
			if (!requireJobPricing("item-insert")) return;

			template.template_items.forEach((item) => {
				if (item.tariffs_default_id || item.tariffs_custom_id) {
					const tariffId =
						item.tariffs_custom_id || item.tariffs_default_id;
					for (let i = 0; i < item.quantity; i++) {
						addJobItem(
							{ code_e: item.code_e || "", id: tariffId || null },
							jobDocumentId,
							"tariff",
							{ force: false }
						);
					}
				} else if (item.article_id) {
					for (let i = 0; i < item.quantity; i++) {
						addJobItem(
							{
								code_e: item.code_e || "",
								id: item.article_id.toString(),
							},
							jobDocumentId,
							"article",
							{ force: false }
						);
					}
				} else if (item.code_e) {
					if (
						tariffsLookup[item.code_e] &&
						Object.keys(tariffsLookup[item.code_e]).length > 0
					) {
						for (let i = 0; i < item.quantity; i++) {
							addJobItem(
								{ code_e: item.code_e, id: null },
								jobDocumentId,
								"tariff",
								{ force: false }
							);
						}
					} else {
						for (let i = 0; i < item.quantity; i++) {
							addJobItem(
								{ code_e: item.code_e, id: null },
								jobDocumentId,
								"article",
								{ force: false }
							);
						}
					}
				}
			});
		}
	};

	const handleRowDelete = (rowIds: string[]) => {
		if (addJobItemAllowed(status)) deleteJobItems(rowIds, jobDocumentId);
	};

	const handlePasteRows = (
		pastedRows: any[],
		metaKeyPressed: boolean = false
	) => {
		if (addJobItemAllowed(status)) {
			pasteJobItems(pastedRows, jobDocumentId, metaKeyPressed);
		}
	};

	const handleCutOrDeleteRows = (cutRows: any[]) => {
		if (addJobItemAllowed(status))
			handleRowDelete(cutRows.map((row) => row.id));
	};

	const handleConfigChange = async (newConfig: TableConfig) => {
		updateGridConfig(newConfig);
	};

	const canAddItems = addJobItemAllowed(status);

	const handleAddTariffByCode = (value: string, force: boolean = false) => {
		if (!canAddItems) {
			showNotification({
				message: "Tarif können nicht hinzugefügt werden",
				type: "error",
			});
			return;
		}
		addJobItem({ code_e: value, id: null }, jobDocumentId, "tariff", {
			force,
		});
	};

	const handleAddArticleByCode = (value: string, force: boolean = false) => {
		if (!canAddItems) {
			showNotification({
				message: "Artikel können nicht hinzugefügt werden",
				type: "error",
			});
			return;
		}
		addJobItem({ code_e: value, id: null }, jobDocumentId, "article", {
			force,
		});
	};

	const handleAddTariffById = (
		code_e: string,
		id: string,
		options?: { force: boolean }
	) => {
		if (!canAddItems) {
			showNotification({
				message: "Tarif können nicht hinzugefügt werden",
				type: "error",
			});
			return;
		}
		addJobItem(
			{ code_e: code_e, id: id },
			jobDocumentId,
			"tariff",
			options
		);
	};

	const handleAddArticleById = (
		code_e: string,
		id: number,
		options?: { force: boolean }
	) => {
		if (!canAddItems) {
			showNotification({
				message: "Artikel können nicht hinzugefügt werden",
				type: "error",
			});
			return;
		}
		addJobItem(
			{ code_e: code_e, id: id.toString() },
			jobDocumentId,
			"article",
			options
		);
	};

	const toolbarRef = useRef<HTMLDivElement>(null);
	const bulkOpsRef = useRef<HTMLDivElement>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const [bulkOps, setBulkOps] = useState<BulkOpsState | null>(null);

	useEffect(() => {
		const el = toolbarRef.current;
		if (!el) return;
		const ro = new ResizeObserver(() => {
			const height = el.getBoundingClientRect().height;
			wrapperRef.current?.style.setProperty(
				"--toolbar-height",
				`${height}px`
			);
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	useEffect(() => {
		const el = bulkOpsRef.current;
		if (!el) {
			wrapperRef.current?.style.setProperty("--bulk-ops-height", "0px");
			return;
		}
		const ro = new ResizeObserver(() => {
			const height = el.getBoundingClientRect().height;
			wrapperRef.current?.style.setProperty(
				"--bulk-ops-height",
				`${height}px`
			);
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, [bulkOps]);

	return (
		<div ref={wrapperRef} className="sticky-grid-header">
			<div
				ref={toolbarRef}
				className="sticky z-10 bg-white border-b border-gray-200 py-1 px-1"
				style={{
					top: "var(--doc-header-height, 0px)",
					boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)",
				}}
			>
				<Toolbar
					onAddTariffByCode={handleAddTariffByCode}
					onAddArticleByCode={handleAddArticleByCode}
					onAddTariffById={handleAddTariffById}
					onAddArticleById={handleAddArticleById}
					onAddTemplate={handleAddTemplate}
					jobItemCodes={jobItems}
					jobDocumentId={jobDocumentId}
					jobDocumentType={jobDocumentType}
					itemSelectionDialog={itemSelectionDialog}
					handleItemSelection={handleItemSelection}
					hasRecentJobItemsInClipboard={hasRecentJobItemsInClipboard}
					clipboardData={clipboardData.data}
					onPaste={handlePasteRows}
					canAddItems={canAddItems}
					onShowShortcuts={() => setShowShortcuts(true)}
					total={total}
				/>
			</div>
			{bulkOps && (
				<div
					ref={bulkOpsRef}
					className="sticky z-10 bg-white border-b border-gray-200 py-1.5 px-2 flex items-center gap-2"
					style={{
						top: "calc(var(--doc-header-height, 0px) + var(--toolbar-height, 0px))",
						boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)",
					}}
					data-testid="bulk-ops-bar"
				>
					<button
						onClick={bulkOps.handleDeselectAll}
						className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 rounded hover:bg-gray-100"
						aria-label="Auswahl aufheben"
						data-testid="bulk-ops-deselect"
					>
						<X className="w-3 h-3" />
						{bulkOps.selectedCount} ausgewählt
					</button>
					<button
						onClick={bulkOps.handleCopy}
						className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100"
						aria-label="Ausgewählte Zeilen kopieren"
					>
						Kopieren
					</button>
					<button
						onClick={bulkOps.handleCut}
						className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100"
						aria-label="Ausgewählte Zeilen ausschneiden"
					>
						Ausschneiden
					</button>
					{bulkOps.hasClipboardData && (
						<button
							onClick={(e) =>
								bulkOps.handlePaste(e.metaKey || e.ctrlKey)
							}
							className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100"
							aria-label="Zeilen einfügen"
						>
							Einfügen
						</button>
					)}
					<button
						onClick={bulkOps.handleDelete}
						className="px-2 py-1 text-xs border border-red-300 rounded hover:bg-red-50 text-red-600"
						aria-label="Ausgewählte Zeilen löschen"
					>
						Löschen
					</button>
				</div>
			)}
			<CustomTable
				rows={jobItems}
				userConfig={gridConfig}
				defaultConfig={jobItemsTableConfigDefault}
				onConfigChange={handleConfigChange}
				onRowChange={handleRowChange}
				onSelectedChange={(selected) => {
					Logger.log("onSelectedChange", selected);
				}}
				onPasteRows={handlePasteRows}
				onCutRows={handleCutOrDeleteRows}
				dataCompatibility={["job_items"]}
				onDeleteRows={handleCutOrDeleteRows}
				disabled={disabled}
				externalBulkOps
				onBulkOpsStateChange={setBulkOps}
				onRowReorder={
					canAddItems && !disabled
						? (orderedIds) =>
								reorderJobItems(jobDocumentId, orderedIds)
						: undefined
				}
			/>
			{!disabled && (
				<CodeSuggestions
					tariffs={tariffs}
					jobItemCodes={jobItems.map((item) => item.code_e)}
					canAddItems={canAddItems}
					jobDocumentId={jobDocumentId}
				/>
			)}
			<JobItemsShortcutsDialog
				open={showShortcuts}
				onClose={() => setShowShortcuts(false)}
			/>
		</div>
	);
};

// TODO: NON-DEMO add density selection
// import { CustomDensitySelector } from "../../../../components/custom-density-selector.component";
// <CustomDensitySelector density="standard" onChange={() => {}} />

export const Toolbar: React.FC<{
	onAddTariffByCode: (value: string, force?: boolean) => void;
	onAddArticleByCode: (value: string, force?: boolean) => void;
	onAddTariffById: (
		code_e: string,
		id: string,
		options?: { force: boolean }
	) => void;
	onAddArticleById: (
		code_e: string,
		id: number,
		options?: { force: boolean }
	) => void;
	onAddTemplate: (value: string) => void;
	jobItemCodes: { code_e: string }[];
	jobDocumentId: number;
	jobDocumentType: JobDocumentTypeEnum | "template";
	itemSelectionDialog: any;
	handleItemSelection: (
		item: TariffWithPricesViewType | ExtendedArticleEntityType | null
	) => void;
	hasRecentJobItemsInClipboard: boolean;
	clipboardData: any[];
	onPaste: (pastedRows: any[], metaKeyPressed: boolean) => void;
	canAddItems: boolean;
	onShowShortcuts?: () => void;
	total?: { value: number; text: string };
}> = ({
	onAddTariffByCode,
	onAddArticleByCode,
	onAddTariffById,
	onAddArticleById,
	onAddTemplate,
	jobItemCodes,
	jobDocumentType,
	jobDocumentId,
	itemSelectionDialog,
	handleItemSelection,
	hasRecentJobItemsInClipboard,
	clipboardData,
	onPaste,
	canAddItems,
	onShowShortcuts,
	total,
}) => {
	const { articles: articleToSort } = useCentralStore((state) => ({
		articles: state.articles,
	}));
	const { tariffsLookup: tariffsToSort } = useCatalogsStore((state) => ({
		tariffsLookup: state.tariffsLookup,
	}));
	const { templatesLookup } = useTemplatesStore((state) => ({
		templatesLookup: state.templatesLookup,
	}));

	const articles = useMemo(() => {
		// Flatten nested structure: code_e -> id -> article
		const flattened: ExtendedArticleEntityType[] = [];
		for (const codeE in articleToSort) {
			for (const id in articleToSort[codeE]) {
				flattened.push(articleToSort[codeE][id]);
			}
		}
		return flattened.sort((a, b) => {
			const codeA = a.code_e || "";
			const codeB = b.code_e || "";
			return codeA.localeCompare(codeB);
		});
	}, [articleToSort]);
	const tariffs = useMemo(() => {
		const flattened: TariffWithPricesViewType[] = [];
		for (const codeE in tariffsToSort) {
			const entries = Object.values(tariffsToSort[codeE]);
			const customs = entries.filter((t) => t.is_custom);
			flattened.push(...(customs.length > 0 ? customs : entries));
		}
		return flattened.sort((a, b) => {
			const codeA = a.code_e || "";
			const codeB = b.code_e || "";
			return codeA.localeCompare(codeB);
		});
	}, [tariffsToSort]);

	const { client, organization } = useCentralStore((state) => ({
		client: state.client,
		organization: state.organization,
	}));

	const { openSidebar, openedSidebar, closeSidebar, job } = useJobStore(
		(state) => ({
			openSidebar: state.openSideBar,
			openedSidebar: state.openedSidebar,
			closeSidebar: state.closeSidebar,
			job: state.job,
		})
	);
	const openTariffsSidebar = () => openSidebar(jobDocumentId, "tariff");
	const openArticlesSidebar = () => openSidebar(jobDocumentId, "article");
	const openTemplatesSidebar = () => openSidebar(jobDocumentId, "template");
	const openCatalogSidebar = () => openSidebar(jobDocumentId, "catalog");

	const catalogTree = useServiceCatalogStore((s) => s.catalog.tree);
	const hasCatalog = catalogTree.length > 0;
	const catalogOnlyMode =
		hasCatalog &&
		hasConfigValue(organization?.config, "catalog_only_mode", true);

	const handlePasteClick = (event: React.MouseEvent) => {
		if (canAddItems && clipboardData.length > 0) {
			const metaKeyPressed = event.metaKey || event.ctrlKey;
			onPaste(clipboardData, metaKeyPressed);
		}
	};

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "row",
				alignItems: "center",
				width: "100%",
			}}
		>
			<div className="flex gap-2">
				{!catalogOnlyMode &&
					jobDocumentType !== JobDocumentTypeEnum.Tracing && (
						<div className="flex gap-4">
							<TarifcodeButton
								onAddTariffByCode={onAddTariffByCode}
								onSearchClick={openTariffsSidebar}
							/>
						</div>
					)}
				{!catalogOnlyMode && (
					<div>
						<ArticleCodeButton
							onAddArticleByCode={onAddArticleByCode}
							onSearchClick={openArticlesSidebar}
						/>
					</div>
				)}
				{!catalogOnlyMode &&
					jobDocumentType !== JobDocumentTypeEnum.Tracing && (
						<div>
							<TemplatecodeButton
								onSubmit={(val) => {
									onAddTemplate(val);
								}}
								onSearchClick={openTemplatesSidebar}
							/>
						</div>
					)}
				{hasCatalog &&
					jobDocumentType !== JobDocumentTypeEnum.Tracing && (
						<Button
							variant={
								openedSidebar?.type === "catalog"
									? "default"
									: "outline"
							}
							size="sm"
							onClick={openCatalogSidebar}
							aria-label="Leistungskatalog öffnen"
							data-testid="catalog-sidebar-button"
							className="h-7"
						>
							<FolderTree className="h-4 w-4 mr-1" />
							Katalog
						</Button>
					)}
				{canAddItems && <DefaultTechnicianSelect />}
				{onShowShortcuts && (
					<Button
						variant="ghost"
						size="icon"
						onClick={onShowShortcuts}
						aria-label="Tastenkürzel anzeigen"
						data-testid="show-shortcuts-button"
						title="Tastenkürzel"
						className="h-7 w-7"
					>
						<Keyboard className="h-4 w-4" />
					</Button>
				)}
			</div>
			{hasRecentJobItemsInClipboard && (
				<Button
					variant="default"
					size="sm"
					onClick={handlePasteClick}
					disabled={!canAddItems}
					aria-label="Positionen aus Zwischenablage einfügen"
					data-testid="paste-job-items-button"
					className="ml-2 h-[27px] text-sm font-semibold px-3"
				>
					<ClipboardPaste className="w-4 h-4 mr-1.5" />
					Positionen Einfügen
				</Button>
			)}
			{jobDocumentType !== JobDocumentTypeEnum.Tracing && (
				<JobItemsTariffsSelection
					jobItemCodes={jobItemCodes}
					rows={tariffs}
					onAddTariffById={onAddTariffById}
					jobDocumentId={jobDocumentId}
					isNewStyle={jobDocumentType === "template"}
					defaultCatalogId={
						client?.default_catalogs_default_id ||
						client?.default_catalogs_custom_id ||
						organization?.default_catalogs_default_id ||
						organization?.default_catalogs_custom_id ||
						null
					}
				/>
			)}
			<JobItemsArticlesSelection
				rows={articles}
				label="Artikel"
				onAddArticleById={onAddArticleById}
				jobItemCodes={jobItemCodes}
				jobDocumentId={jobDocumentId}
				isNewStyle={jobDocumentType === "template"}
			/>
			{jobDocumentType !== JobDocumentTypeEnum.Tracing && (
				<TemplateSelectDialog
					open={
						openedSidebar?.type === "template" &&
						openedSidebar?.documentId === jobDocumentId
					}
					setOpen={(open) => {
						if (!open) {
							closeSidebar();
						}
					}}
					onSelect={(templateId) => {
						const template = templatesLookup[templateId];
						if (template) {
							onAddTemplate(template.code);
						} else {
							showNotification({
								message: "Vorlage nicht gefunden",
								type: "error",
							});
						}
					}}
					priceListId={job?.price_list_id ?? null}
					tpValue={job?.tp_value ?? 1}
					taxRate={job?.tax_rate ?? 8.1}
					roundTo={
						(job?.round_to as RoundToType) ?? RoundToType.TO_RAPPEN
					}
				/>
			)}
			{itemSelectionDialog &&
				itemSelectionDialog.jobDocumentId === jobDocumentId && (
					<TariffSelectionDialog
						open={itemSelectionDialog.open}
						items={itemSelectionDialog.items}
						code={itemSelectionDialog.code}
						onSelect={(item) => handleItemSelection(item)}
						onCancel={() => handleItemSelection(null)}
					/>
				)}
			{jobDocumentType !== JobDocumentTypeEnum.Tracing && (
				<JobItemsServiceCatalogSelection
					jobDocumentId={jobDocumentId}
					onAddTariffById={onAddTariffById}
					onAddArticleById={onAddArticleById}
					onAddTemplate={onAddTemplate}
				/>
			)}
			{total && jobDocumentType !== JobDocumentTypeEnum.Tracing && (
				<span
					className="ml-auto inline-flex items-center gap-1 px-2 cursor-default uppercase font-medium tracking-wide text-xs"
					title="Details im Total-Tab im oberen Panel"
					data-testid="job-document-total"
				>
					<Calculator className="h-4 w-4" />
					{total.value === 0 ? "0.00" : total.text}
				</span>
			)}
		</div>
	);
};

const CodeButton: React.FC<{
	onSubmit: (value: string, force?: boolean) => void;
	placeholder: string;
	width: string;
	onSearchClick?: () => void;
}> = ({ onSubmit, placeholder, width, onSearchClick }) => {
	const [value, setValue] = useState<string>("");
	const inputRef = useRef<HTMLInputElement>(null);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setValue(event.target.value);
	};

	const handleClick = (force: boolean = false) => {
		if (value) {
			onSubmit(value, force);
		} else {
			showNotification({
				message: "Bitte einen Wert eingeben",
				type: "error",
			});
		}
	};

	const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			event.preventDefault();
			event.stopPropagation();
			const force = event.metaKey || event.ctrlKey;
			handleClick(force);
			inputRef.current?.select();
		}
	};

	return (
		<div className="relative inline-flex" style={{ width }}>
			<Input
				ref={inputRef}
				value={value || ""}
				onChange={handleChange}
				onFocus={(e) => e.target.select()}
				onBlur={() => setValue("")}
				onKeyDown={handleKeyPress}
				placeholder={placeholder}
				aria-label={`${placeholder}-Code eingeben`}
				data-testid={`code-input-${placeholder.toLowerCase()}`}
				className="h-7 text-sm pr-9"
			/>
			{onSearchClick && (
				<Button
					variant="ghost"
					size="icon"
					onClick={onSearchClick}
					data-testid="code-button-search-icon"
					className="absolute right-0 top-0 h-7 w-8 border-l rounded-l-none hover:bg-accent"
				>
					<List className="h-4 w-4 text-primary" />
				</Button>
			)}
		</div>
	);
};

export const ArticleCodeButton: React.FC<{
	onAddArticleByCode: (value: string, force?: boolean) => void;
	onSearchClick?: () => void;
}> = ({ onAddArticleByCode, onSearchClick }) => {
	return (
		<CodeButton
			onSubmit={onAddArticleByCode}
			placeholder="Artikel"
			width="110px"
			onSearchClick={onSearchClick}
		/>
	);
};

export const TarifcodeButton: React.FC<{
	onSearchClick?: () => void;
	onAddTariffByCode: (value: string, force?: boolean) => void;
}> = ({ onSearchClick, onAddTariffByCode }) => {
	return (
		<CodeButton
			onSubmit={onAddTariffByCode}
			placeholder="Tarif"
			width="110px"
			onSearchClick={onSearchClick}
		/>
	);
};

export const TemplatecodeButton: React.FC<{
	onSubmit: (value: string) => void;
	onSearchClick?: () => void;
}> = ({ onSubmit, onSearchClick }) => (
	<CodeButton
		onSubmit={onSubmit}
		placeholder="Vorlage"
		width="110px"
		onSearchClick={onSearchClick}
	/>
);
